import { ajax } from 'rxjs/ajax'
import { catchError } from 'rxjs/operators'

import { baseURL, handleError, requestHeader } from './api-routes.common'

type BulkUploadRowValue = string | number | boolean | null | undefined | Date

export type BulkUploadRow = Record<string, BulkUploadRowValue>

export interface BulkUploadResponse<T = BulkUploadRow[]> {
  message?: string
  data?: T
  error?: string
}

export interface SalesHistoryDate {
  date: string
  timezone_type: number
  timezone: string
}

export interface SalesHistoryItem {
  id: number
  ems_customer_id: number
  sale_channel: string
  sale_year: number
  sale_month: number
  total_products: number
  total_qty_sold: number
  growth_factor: number
  created_at: SalesHistoryDate
  updated_at: SalesHistoryDate | null
}

export interface SalesHistoryMeta {
  total: number
  page: number
  page_size: number
  pages: number
}

export interface SalesHistoryResponse {
  data: SalesHistoryItem[]
  _meta: SalesHistoryMeta
}

export interface SalesHistoryProductInput {
  product_sku: string
  product_name: string
  qty_sold: number
}

// Detail endpoint returns line items for a single sales_history record
export interface SalesHistoryDetailItem {
  id: number
  sales_history_id: number
  product_id: number
  product_sku: string
  product_name: string
  qty_sold: number
  created_at: SalesHistoryDate
  updated_at: SalesHistoryDate | null
}

export interface SalesHistoryDetailResponse {
  data: SalesHistoryDetailItem[]
}

export interface LeadTimeProduct {
  product_id: number
  product_image_path?: string
  product_name: string
  product_sku: string
}

export interface LeadTimeItem {
  id: number
  ems_customer_id: number
  product: LeadTimeProduct
  product_buffer_stock: number
  product_lead_time: number
  product_moq: number
}

export interface LeadTimeRow {
  id?: number
  product: LeadTimeProduct
  product_buffer_stock: number
  lead_time: number
  moq: number
  // Flattened product fields for table convenience
  product_id?: number
  product_image_path?: string
  product_name?: string
  product_sku?: string
  // Optional fields used when editing in the UI
  new_lead_time?: number
  new_moq?: number
  // Expanded row state
  isExpanded?: boolean
  bomItems?: ProductBomItem[]
  isBomLoading?: boolean
  bomError?: string | null
}

export interface LeadTimeMeta {
  total: number
}

export interface LeadTimeResponse {
  data: LeadTimeItem[]
  _meta: LeadTimeMeta
}

export interface SubmitBulkSalesPayload {
  sale_channel: string
  sale_year: number
  sale_month: number
  growth_factor: number
  products: SalesHistoryProductInput[]
}

const buildQueryString = (
  params?: Record<string, string | number | boolean | undefined>,
) => {
  if (!params) {
    return ''
  }

  const searchParams = new URLSearchParams()
  Object.keys(params).forEach((key) => {
    const value = params[key]
    if (value === undefined || value === null || value === '') {
      return
    }

    searchParams.append(key, String(value))
  })

  const query = searchParams.toString()
  return query ? `?${query}` : ''
}

export const submitBulkProductUpload = (rows: BulkUploadRow[]) =>
  ajax<BulkUploadResponse>({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/products/bulk/upload`,
    body: {
      data: rows,
    },
  }).pipe(catchError(handleError('submitBulkProductUpload')))

export const submitBulkSalesUpload = (payload: SubmitBulkSalesPayload) =>
  ajax<BulkUploadResponse>({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/sales-history`,
    body: payload,
  }).pipe(catchError(handleError('submitBulkSalesUpload')))

export const getSalesHistory = (
  filters?: Record<string, string | number | boolean | undefined>,
) =>
  ajax<SalesHistoryResponse>({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/sales-history${buildQueryString(filters)}`,
  }).pipe(catchError(handleError('getSalesHistory')))

export const getSalesHistoryDetail = (id: number | string) =>
  ajax<SalesHistoryDetailResponse>({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/sales-history/${id}`,
  }).pipe(catchError(handleError('getSalesHistoryDetail')))

export const getLeadTimes = (
  filters?: Record<string, string | number | boolean | undefined>,
) =>
  ajax<LeadTimeResponse>({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/product-lead-time${buildQueryString(
      filters,
    )}`,
  }).pipe(catchError(handleError('getLeadTimes')))

export const submitLeadTimes = (rows: LeadTimeRow[], productType: string) =>
  ajax<BulkUploadResponse>({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/product-lead-time`,
    body: {
      product_type: productType,
      products: rows.map((row) => ({
        product_sku: row.product?.product_sku || '',
        product_lead_time: row.lead_time,
        product_moq: row.moq,
      })),
    },
  }).pipe(catchError(handleError('submitLeadTimes')))

export const updateLeadTime = (id: number, row: LeadTimeRow) =>
  ajax<BulkUploadResponse>({
    headers: requestHeader(),
    method: 'PUT',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/product-lead-time/${id}`,
    body: row,
  }).pipe(catchError(handleError('updateLeadTime')))

export interface BomProduct {
  product_id: number
  product_image_path?: string
  product_name: string
  product_sku: string
}

export interface BomDate {
  date: string
  timezone_type: number
  timezone: string
}

export interface ProductBomItem {
  id: number
  ems_customer_id: number
  finished_good: BomProduct
  component: BomProduct
  quantity_required: number
  notes?: string
  created_by: number
  created_at: BomDate
  updated_at: BomDate | null
}

export interface ProductBomResponse {
  success?: boolean
  data?: ProductBomItem[]
  error?: string
}

export interface CreateBomComponent {
  component_sku: string
  quantity_required: number
  notes?: string
}

export interface CreateBomPayload {
  components: CreateBomComponent[]
}

export interface UpdateBomPayload {
  quantity_required?: number
  notes?: string
}

export const getProductBom = (productId: number | string) =>
  ajax<ProductBomResponse>({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/products/${productId}/bom`,
  }).pipe(catchError(handleError('getProductBom')))

export const createProductBom = (
  productId: number | string,
  payload: CreateBomPayload,
) =>
  ajax<BulkUploadResponse>({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/products/${productId}/bom`,
    body: payload,
  }).pipe(catchError(handleError('createProductBom')))

export const updateProductBom = (
  bomId: number | string,
  payload: UpdateBomPayload,
) =>
  ajax<BulkUploadResponse>({
    headers: requestHeader(),
    method: 'PUT',
    withCredentials: false,
    url: `${baseURL}api/v2/products/bom/${bomId}`,
    body: payload,
  }).pipe(catchError(handleError('updateProductBom')))

export const deleteProductBom = (bomId: number | string) =>
  ajax<BulkUploadResponse>({
    headers: requestHeader(),
    method: 'DELETE',
    withCredentials: false,
    url: `${baseURL}api/v2/products/bom/${bomId}`,
  }).pipe(catchError(handleError('deleteProductBom')))

export interface WarehouseStockItem {
  warehouse_name: string
  stock: number
}

export interface WarehouseStock {
  warehouses: WarehouseStockItem[]
  total: number
}

export interface DemandForecastItem {
  product_id?: number
  product_sku: string
  product_name: string
  product_image?: string
  lead_time: number
  moq: number
  avg_sales_6m: number
  buffer_stock: number
  growth_factor: number
  expected_sales: number
  warehouse_stock: WarehouseStock
  in_transit_stock: number
  required_stock: number
}

export interface DemandForecastMeta {
  target_month: number
  target_year: number
  sale_channel: string
  product_type: string
  total_products: number
  growth_factor: number
}

export interface DemandForecastResponse {
  data?: DemandForecastItem[]
  _meta?: DemandForecastMeta
  error?: string
}

export const getDemandForecast = (
  filters?: Record<string, string | number | boolean | undefined>,
) =>
  ajax<DemandForecastResponse>({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/planner${buildQueryString(filters)}`,
  }).pipe(catchError(handleError('getDemandForecast')))

export interface PlannerBomWarehouse {
  warehouse_name: string
  stock: number
}

export interface PlannerBomItem {
  component_product_id: number
  component_sku: string
  component_name: string
  quantity_required_per_unit: number
  required_bom_qty: number
  warehouse: PlannerBomWarehouse[]
  bom_in_stock: number
  in_transit_stock: number
  lead_time: number
  moq: number
}

export interface PlannerBomMeta {
  finished_good_product_id: number
  required_stock: number
  total_components: number
}

export interface PlannerBomResponse {
  data?: PlannerBomItem[]
  _meta?: PlannerBomMeta
  error?: string
}

export const getPlannerBom = (
  productId: number | string,
  requiredStock?: number,
) =>
  ajax<PlannerBomResponse>({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/planner/bom/${productId}${buildQueryString(
      requiredStock !== undefined
        ? { required_stock: requiredStock }
        : undefined,
    )}`,
  }).pipe(catchError(handleError('getPlannerBom')))
